import { DataTable } from "@/components/data-table/data-table";
import type { PaginatedAuditLogsResponse } from "@/features-root-user/service/audit";
import { columns } from "./columns";
import { useMemo, useCallback } from "react";
import { z } from "zod";

export const organizationAuditOperationType = z.enum([
  "INSERT",
  "UPDATE",
  "DELETE",
]);

interface AuditTableProps {
  data: PaginatedAuditLogsResponse;
  onPaginationChange: (page: number, pageSize: number) => void;
  onFilterChange?: (filters: Record<string, unknown>) => void;
}

export function AuditTable({
  data,
  onPaginationChange,
  onFilterChange,
}: AuditTableProps) {
  const pageCount = useMemo(() => {
    return Math.ceil(data.totalCount / data.pageSize);
  }, [data.totalCount, data.pageSize]);

  // Define filter options
  const operationTypeOptions = useMemo(
    () =>
      Object.values(organizationAuditOperationType.enum).map((value) => ({
        label: value,
        value,
      })),
    [],
  );

  // Define date filter options
  const dateFilterOptions = useMemo(() => {
    return [
      { label: "Today", value: "today" },
      { label: "Yesterday", value: "yesterday" },
      { label: "Last 7 days", value: "last7days" },
      { label: "Last 30 days", value: "last30days" },
      { label: "This month", value: "thismonth" },
      { label: "Last month", value: "lastmonth" },
    ];
  }, []);

  // Create users filter options
  const userFilterOptions = useMemo(() => {
    const uniqueUsers = Array.from(
      new Set(data.logs.map((log) => log.updatedByDisplayName)),
    )
      .sort()
      .map((name) => ({
        label: name,
        value: name,
      }));
    return uniqueUsers;
  }, [data.logs]);

  const handleColumnFiltersChange = useCallback(
    (filters: Record<string, unknown>) => {
      onFilterChange?.(filters);
    },
    [onFilterChange],
  );

  return (
    <div className="-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0">
      <DataTable
        tableId="root-user-audit-logs"
        data={data.logs}
        columns={columns}
        // Remove searchColumn to hide search bar
        facetedFilters={[
          {
            columnId: "operationType",
            title: "Operation Type",
            options: operationTypeOptions,
          },
          {
            columnId: "updatedAt",
            title: "Date Range",
            options: dateFilterOptions,
          },
          {
            columnId: "organizationName",
            title: "Organization",
            options: Array.from(
              new Set(
                data.logs.map((log) => log.organizationName || "Unknown"),
              ),
            )
              .filter(Boolean)
              .map((org) => ({
                label: org,
                value: org,
              })),
          },
          {
            columnId: "updatedByDisplayName",
            title: "Updated By",
            options: userFilterOptions,
          },
        ]}
        manualPagination={true} // Enable manual pagination
        currentPage={data.page}
        pageSize={10} // Fixed page size of 10
        onPaginationChange={(
          updater:
            | { pageIndex: number; pageSize: number }
            | ((state: { pageIndex: number; pageSize: number }) => {
                pageIndex: number;
                pageSize: number;
              }),
        ) => {
          const newState =
            typeof updater === "function"
              ? updater({ pageIndex: data.page - 1, pageSize: 10 })
              : updater;
          // Always use pageSize 10 regardless of what's in the updater
          onPaginationChange(newState.pageIndex + 1, 10);
        }}
        rowCount={data.totalCount}
        onColumnFiltersChange={handleColumnFiltersChange}
        useFixedPageSizePagination={true}
      />
    </div>
  );
}
